import TopSection from "../../LT/TopSection";
import Cards from "../../../components/common/card/Card";
import "./ARDashboard.css";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Badge, IconButton, Popover } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dropdown from "../../../components/common/Dropdown";
import { MultiDropdown } from "../../../components/common/MultiDropdown";
import { ARDashboardColumns } from "./ARDashboardColumns";
import DataTable from "../../../components/common/datatable/DataTable";
import { Loader } from "../../../components/common/Loader";
import ButtonComponent from "../../../components/common/button/Button";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAfcFilters } from "../../../redux/actions/afcFiltersSlice";
// import { fetchAfcRiskCount } from "../../../redux/actions/afcRiskCountSlice";
import { saveRiskAuditHistory } from "../../../redux/actions/userDefinedRiskStatusSlice";
import { getAfcFilters } from "../../../common/endpoints";
// import { getAfcRiskCount } from "../../../common/endpoints";
import { toast } from "react-toastify";
import { VariantViewModal } from "../VariantView/VariantViewModal";
import { ShowVariantViewModal } from "../VariantView/ShowVariantViewModal";

const createInitialFilters = () => ({
  plant: [],
  supplier: "ALL",
  material: [],
  matGroup: [],
  purGroup: [],
  scode: [],
  mrpCtrl: [],
  gmp: [],
  country: [],
  region: [],
});

const cloneFilterItems = (items = []) =>
  (items || []).map((item) => (item && typeof item === "object" ? { ...item } : item));

const hasAppliedDropdownFilters = (state) => {
  const safe = state || {};
  return Boolean(
    (safe.plant || []).length ||
      (safe.material || []).length ||
      (safe.matGroup || []).length ||
      (safe.purGroup || []).length ||
      (safe.scode || []).length ||
      (safe.mrpCtrl || []).length ||
      (safe.gmp || []).length ||
      (safe.country || []).length ||
      (safe.region || []).length ||
      (safe.supplier && safe.supplier !== "ALL")
  );
};

const cloneFiltersState = (state) => {
  if (!state) return createInitialFilters();
  return {
    plant: cloneFilterItems(state.plant),
    supplier: state.supplier ?? "ALL",
    material: cloneFilterItems(state.material),
    matGroup: cloneFilterItems(state.matGroup),
    purGroup: cloneFilterItems(state.purGroup),
    scode: cloneFilterItems(state.scode),
    mrpCtrl: cloneFilterItems(state.mrpCtrl),
    gmp: cloneFilterItems(state.gmp),
    country: cloneFilterItems(state.country),
    region: cloneFilterItems(state.region),
  };
};

const DASHBOARD_FILTERS_STORAGE_KEY = "dashboardFilters";

const readDashboardFiltersFromStorage = () => {
  try {
    const raw = localStorage.getItem(DASHBOARD_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const safeGridFilterModel =
      parsed.gridFilterModel && typeof parsed.gridFilterModel === "object"
        ? parsed.gridFilterModel
        : null;
    return {
      filters: cloneFiltersState(parsed.filters),
      appliedFilters: cloneFiltersState(parsed.appliedFilters),
      riskFilter: parsed.riskFilter || "ALL",
      gridFilterModel: safeGridFilterModel,
    };
  } catch {
    return null;
  }
};

const writeDashboardFiltersToStorage = (payload) => {
  try {
    localStorage.setItem(DASHBOARD_FILTERS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures (private mode/quota) and keep app usable.
  }
};

const removeDashboardFiltersFromStorage = () => {
  try {
    localStorage.removeItem(DASHBOARD_FILTERS_STORAGE_KEY);
  } catch {
    // no-op
  }
};

let dashboardFiltersCache = null;
let dashboardAppliedFiltersCache = null;
let dashboardRiskFilterCache = "ALL";
const dashboardTotalCountCache = new Map();
const dashboardLoadRequestCache = new Map();
const INITIAL_SUMMARY_PAGE_SIZE = 2000;


const baseCards = [
  { title: "High Risk", value: 0, name: "highRisk", url: null },
  { title: "Medium Risk", value: 0, name: "mediumRisk", url: null },
  { title: "Low Risk", value: 0, name: "lowRisk", url: "/dashboard/lowrisk" },
];

const pagination = true;
const paginationPageSize = 500;
const paginationPageSizeSelector = [500, 1000, 1500, 2000];
const TABLE_TYPE = "FORECAST";
const normalizeRole = (role) => (role || "").replace(/\s+/g, "").toLowerCase();
const hasRole = (rawRole, target) =>
  normalizeRole(rawRole).split(",").some((r) => r === target);
const normalizeRiskValue = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (v === "HIGH" || v === "MEDIUM" || v === "LOW") return v;
  return "";
};

const normalizeUpdatedByEntries = (value) => {
  const toEntry = (item) => {
    if (!item || typeof item !== "object") return null;
    const name = item.name ?? item.userName ?? item.email ?? "";
    const updatedAt = item.updatedAt ?? item.timestamp ?? item.time ?? "";
    if (!name && !updatedAt) return null;
    return {
      name,
      updatedAt,
    };
  };

  if (Array.isArray(value)) {
    return value.map(toEntry).filter(Boolean);
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value.updatedBy)) {
      return normalizeUpdatedByEntries(value.updatedBy);
    }
    const singleEntry = toEntry(value);
    return singleEntry ? [singleEntry] : [];
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== "[" && trimmed[0] !== "{")) return [];
    try {
      return normalizeUpdatedByEntries(JSON.parse(trimmed));
    } catch {
      return [];
    }
  }

  return [];
};


const ARDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const outletCtx = useOutletContext();
  const setRiskMaterials = outletCtx?.setRiskMaterials;
  const rawRole = localStorage.getItem("appUserRole") || "";
  const canEditUserDefinedRisk = hasRole(rawRole, "appadmin") || hasRole(rawRole, "globalplanner") || hasRole(rawRole, "planner");

  const initialDashboardState = useMemo(() => {
    const stored = readDashboardFiltersFromStorage();
    return {
      filters: stored?.filters ?? (dashboardFiltersCache ? cloneFiltersState(dashboardFiltersCache) : createInitialFilters()),
      appliedFilters: stored?.appliedFilters ?? (dashboardAppliedFiltersCache ? cloneFiltersState(dashboardAppliedFiltersCache) : createInitialFilters()),
      riskFilter: stored?.riskFilter || dashboardRiskFilterCache || "ALL",
      gridFilterModel: stored?.gridFilterModel || null,
    };
  }, []);

  const [riskFilter, setRiskFilter] = useState(() => initialDashboardState.riskFilter);
  const [filters, setFilters] = useState(() => initialDashboardState.filters);
  const [appliedFilters, setAppliedFilters] = useState(() => initialDashboardState.appliedFilters);

  const [cardsData, setCardsData] = useState(baseCards);
  const [allRows, setAllRows] = useState([]);
  const [feedRows, setFeedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [userDefinedRiskEdits, setUserDefinedRiskEdits] = useState({});
  const [riskRowSaving, setRiskRowSaving] = useState({});
  const [pageSize, setPageSize] = useState(paginationPageSize);
  const [gridHasFilters, setGridHasFilters] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState(() => initialDashboardState.gridFilterModel);
  const [isGridFilterHydrated, setIsGridFilterHydrated] = useState(() => {
    const hasSaved = Boolean(
      initialDashboardState.gridFilterModel &&
        Object.keys(initialDashboardState.gridFilterModel).length
    );
    return !hasSaved;
  });
  const [columnSelectorAnchor, setColumnSelectorAnchor] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [pendingHiddenColumns, setPendingHiddenColumns] = useState([]);
  const [openCreateVariantModal, setOpenCreateVariantModal] = useState(false);
  const [openShowVariantModal, setOpenShowVariantModal] = useState(false);
  const [variantData, setVariantData] = useState({});
  const totalCountCacheRef = useRef(new Map());
  const initialLoadRef = useRef(true);
  const isRestoringGridFiltersRef = useRef(false);
  const hasRestoredGridFiltersRef = useRef(false);
  const getRiskRowKey = useCallback((row) => `${row?.plant || ""}::${row?.material || ""}`, []);

  // const { data: afcRiskCount } = useSelector((state) => state.afcRiskCount);
  const { error: afcFiltersError } = useSelector((state) => state.afcFilters);
  const { error: afcSummaryError } = useSelector((state) => state.afcSummary);

  const updateRiskCardsFromSummary = useCallback((response) => {
    const counts = response?.data || response || {};
    setCardsData([
      { ...baseCards[0], value: Number(counts.highRisk ?? counts.highCount) || 0 },
      { ...baseCards[1], value: Number(counts.mediumRisk ?? counts.mediumCount) || 0 },
      { ...baseCards[2], value: Number(counts.lowRisk ?? counts.lowCount) || 0 },
    ]);
  }, []);

  // getRiskCount API kept here as commented fallback for future use.
  // Default dashboard card counts from risk-count API
  // useEffect(() => {
  //   dispatch(fetchAfcRiskCount({ dynamicEndpoint: `${getAfcRiskCount}` }));
  // }, [dispatch]);

  // useEffect(() => {
  //   const counts = afcRiskCount?.data;
  //   if (!hasAppliedDropdownFilters(appliedFilters)) {
  //     if (counts) {
  //       setCardsData([
  //         { ...baseCards[0], value: Number(counts.highCount ?? counts.highRisk) || 0 },
  //         { ...baseCards[1], value: Number(counts.mediumCount ?? counts.mediumRisk) || 0 },
  //         { ...baseCards[2], value: Number(counts.lowCount ?? counts.lowRisk) || 0 },
  //       ]);
  //     } else {
  //       setCardsData(baseCards);
  //     }
  //   }
  // }, [afcRiskCount, appliedFilters]);

  // Active behavior: card counts come from getSummaryFilterList response.

  // Filters API
  const [filterData, setFilterData] = useState({
    plants: [],
    suppliers: [],
    materials: [],
    purchasingGroups: [],
    materialGroupDescs: [],
    mrpControl: [],
    scodes: [],
    gmps: [],
    country: [],
    region: [],
  });
  const mergeValues = (base = [], selected = []) =>
    Array.from(
      new Set([...(base || []), ...(selected || [])].filter((v) => v !== undefined))
    );

  useEffect(() => {
    dashboardFiltersCache = cloneFiltersState(filters);
    dashboardAppliedFiltersCache = cloneFiltersState(appliedFilters);
    dashboardRiskFilterCache = riskFilter || "ALL";
    writeDashboardFiltersToStorage({
      filters: cloneFiltersState(filters),
      appliedFilters: cloneFiltersState(appliedFilters),
      riskFilter: riskFilter || "ALL",
      gridFilterModel,
    });
  }, [filters, appliedFilters, riskFilter, gridFilterModel]);

  const mapSummaryRow = (row) => {
    if (!row || typeof row !== "object") {
      return row;
    }

    const lower = Object.keys(row).reduce((acc, key) => {
      acc[key.toLowerCase()] = row[key];
      return acc;
    }, {});

    const rawUpdatedBy =
      row.UPDATED_BY ??
      row.updatedBy ??
      row.updated_by ??
      lower.updated_by ??
      [];

    const mapped = {
      plant: row.PLANT ?? row.plant ?? lower.plant ?? "N/A",
      plant_name: row.plant_name ?? row.PLANT_NAME ?? row.plantName ?? lower.plant_name ?? "N/A",
      supplier: row.SUPPLIER ?? row.supplier ?? lower.supplier ?? "N/A",
      material: row.MATERIAL ?? row.material ?? lower.material ?? "N/A",
      descriptions: row.DESCRIPTION ?? row.descriptions ?? row.description ?? lower.descriptions ?? "N/A",
      unit: row.UNIT ?? row.unit ?? lower.unit ?? "N/A",
      pdt: row.PDT ?? row.pdt ?? lower.pdt ?? "N/A",
      sumofforecast:
        row.FORECAST ?? row.TOTAL_SUPPLIER_FORECAST ?? row.sumofforecast ?? row.sumOfForecast ?? lower.sumofforecast ?? "N/A",
      sumconfirmquantity:
        row.CONFIRMATION_QUANTITY ??
        row.TOTAL_SUPPLIER_CONFIRMATION ??
        row.sumconfirmquantity ??
        row.sumConfirmQuantity ??
        lower.sumconfirmquantity ??
        "N/A",
      creationDate:
        row.CONFIRMED_DATE ?? "N/A",
      extractionDate:
        row.EXTRACTION_DATE ??
        row.extractionDate ??
        row.extraction_date ??
        lower.extractiondate ??
        lower.extraction_date ??
        "N/A",
      publicationDate:
        row.PublishedForecastDate ?? "N/A",
      severity: row.RISK_STATUS ?? row.severity ?? lower.severity ?? "N/A",
      userDefinedRiskStatus:
        row.USER_DEFINED_RISK_STATUS ??
        row.userDefinedRiskStatus ??
        row.user_defined_risk_status ??
        lower.user_defined_risk_status ??
        "",
      updatedBy: normalizeUpdatedByEntries(rawUpdatedBy),
      variance:
        row.DIFF_VARIANCE ??
        row.diffVariance ??
        row.VARIANCE ??
        row.variance ??
        lower.diffvariance ??
        lower.variance ??
        "N/A",
      variancePct:
        row.VARIANCE_PCT ??
        row.variancePct ??
        lower.variancepct ??
        lower.variancepercentage ??
        "N/A",
      materialGroupDesc:
        row.MAT_GROUP ?? row.materialGroupDesc ?? row.materialGroup ?? lower.materialgroupdesc ?? "N/A",
      purchasingGroup: row.PUR_GROUP ?? row.purchasingGroup ?? lower.purchasinggroup ?? "N/A",
      scode: row.S_CODE ?? row.scode ?? lower.scode ?? "N/A",
      mrpController: row.MRP_CONTROL ?? row.mrpController ?? lower.mrpcontroller ?? "N/A",
      gmp: row.GMP ?? row.gmp ?? lower.gmp ?? "N/A",
      country: row.country ?? row.COUNTRY ?? lower.country ?? "N/A",
      region: row.region ?? row.REGION ?? lower.region ?? "N/A",
    };

    const hasValue = Object.values(mapped).some(
      (value) => value !== undefined && value !== null && value !== ""
    );
    return hasValue ? mapped : row;
  };

  const normalizeSummaryRows = (summary) => {
    const candidates = [
      summary?.tableData,
      summary?.data?.tableData,
      summary?.data?.data?.tableData,
      summary?.data?.data,
      summary?.data,
      summary?.results,
      summary?.items,
      summary,
    ];
    const rows = candidates.find(Array.isArray) || [];
    return rows.map(mapSummaryRow);
  };

  const hasApiError = Boolean(afcFiltersError || afcSummaryError);
  const errorOverlayTemplate = '<span class="ag-overlay-loading-center" style="font-size:16px;font-weight:600;color:#b91c1c;margin-top:12px">Couldn\'t load data, server error...</span>';

  const applyFilterSelection = (name, items) => {
    const nextItems = items || filters[name] || [];
    const nextApplied = {
      ...appliedFilters,
      [name]: Array.isArray(nextItems) ? nextItems : [],
    };
    setAppliedFilters(nextApplied);
    gridApi?.paginationGoToPage(0);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMultiFilterChange = (name, items) => {
    const cleanedItems = (items || []).filter((item) => item?.title !== "Select All");
    setFilters((prev) => ({
      ...prev,
      [name]: cleanedItems,
    }));
  };

  const handleCardFilter = (index, title) => {
    const t = (title || "").toLowerCase();
    if (t.includes("high")) setRiskFilter("high");
    else if (t.includes("medium")) setRiskFilter("medium");
    else if (t.includes("low")) setRiskFilter("low");
    else setRiskFilter("ALL");
  };

  const restoreGridFiltersToUi = useCallback(
    (api) => {
      if (!api) return;
      const hasSavedGridFilters = Boolean(
        gridFilterModel && Object.keys(gridFilterModel).length
      );

      if (!hasSavedGridFilters) {
        const currentModel = api.getFilterModel?.() || {};
        setGridHasFilters(Object.keys(currentModel).length > 0);
        hasRestoredGridFiltersRef.current = true;
        setIsGridFilterHydrated(true);
        return;
      }

      isRestoringGridFiltersRef.current = true;
      try {
        api.setFilterModel(gridFilterModel);
      } catch (e) {}

      window.setTimeout(() => {
        try {
          api.onFilterChanged();
        } catch (e) {}
        const appliedModel = api.getFilterModel?.() || {};
        const hasApplied = Object.keys(appliedModel).length > 0;
        setGridHasFilters(hasApplied);
        setGridFilterModel(hasApplied ? appliedModel : null);
        isRestoringGridFiltersRef.current = false;
        hasRestoredGridFiltersRef.current = true;
        setIsGridFilterHydrated(true);
      }, 0);
    },
    [gridFilterModel]
  );

  const onGridReady = (params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    hasRestoredGridFiltersRef.current = false;
    const hasSavedGridFilters = Boolean(
      gridFilterModel && Object.keys(gridFilterModel).length
    );
    setIsGridFilterHydrated(!hasSavedGridFilters);
    // Always set the error overlay template
    try {
      params.api.setOverlayNoRowsTemplate(errorOverlayTemplate);
    } catch (e) {}
    // client-side: no datasource or infinite cache to set
    // If there is an API error, show the overlay immediately
    if (hasApiError) {
      try {
        params.api.showNoRowsOverlay();
      } catch (e) {}
    }

    const currentModel = params.api.getFilterModel?.() || {};
    setGridHasFilters(Object.keys(currentModel).length > 0);
  };

  const handleFirstDataRendered = useCallback(
    (params) => {
      if (hasRestoredGridFiltersRef.current) return;
      restoreGridFiltersToUi(params?.api || gridApi);
    },
    [gridApi, restoreGridFiltersToUi]
  );

  useEffect(() => {
    if (!gridApi) return;
    if (showLoader || isGridFilterHydrated) return;
    if (hasRestoredGridFiltersRef.current) return;
    restoreGridFiltersToUi(gridApi);
  }, [gridApi, showLoader, isGridFilterHydrated, restoreGridFiltersToUi]);

  const isHighMedium = (value) => {
    const v = (value || "").toLowerCase();
    return v.includes("high") || v.includes("medium");
  };

  const getEffectiveUserDefinedRisk = useCallback(
    (row) => {
      // Only consider userDefinedRiskStatus when severity is a valid risk level.
      // If severity is N/A, the user-defined value is treated as inactive
      // (matching the disabled dropdown behavior and backend counting logic).
      const severityValue = normalizeRiskValue(row?.severity);
      if (!severityValue) return "";

      const savedValue = normalizeRiskValue(row?.userDefinedRiskStatus);
      if (savedValue) return savedValue;

      return severityValue;
    },
    []
  );

  useEffect(() => {
    if (!setRiskMaterials) return;
    const highMedium = feedRows.filter((r) => isHighMedium(r.severity));
    setRiskMaterials(highMedium);
  }, [feedRows, setRiskMaterials]);

  const toValues = (items = []) => items.map((item) => item?.value ?? item).filter(Boolean);

  const buildFiltersKey = (applied) => {
    const safe = applied || createInitialFilters();
    const normalize = (items = []) =>
      toValues(items)
        .map((val) => String(val))
        .sort()
        .join("|");

    const supplierValue = safe.supplier ?? "ALL";

    return [
      `plant:${normalize(safe.plant)}`,
      `material:${normalize(safe.material)}`,
      `scode:${normalize(safe.scode)}`,
      `mrpCtrl:${normalize(safe.mrpCtrl)}`,
      `country:${normalize(safe.country)}`,
      `region:${normalize(safe.region)}`,
      `purGroup:${normalize(safe.purGroup)}`,
      `matGroup:${normalize(safe.matGroup)}`,
      `gmp:${normalize(safe.gmp)}`,
      `supplier:${supplierValue}`,
    ].join("::");
  };

  const buildRequestBody = (
    nextPageNo,
    nextPageSize,
    overrides = {},
    applied = appliedFilters
  ) => {
    const gmpValues = overrides.GMP ?? toValues(applied.gmp);
    const body = {
      tableType: TABLE_TYPE,
      plant: overrides.plant ?? toValues(applied.plant),
      material: overrides.material ?? toValues(applied.material),
      scode: overrides.scode ?? toValues(applied.scode),
      mrpController: overrides.mrpController ?? toValues(applied.mrpCtrl),
      country: overrides.country ?? toValues(applied.country),
      region: overrides.region ?? toValues(applied.region),
      purchasingGroup: overrides.purchasingGroup ?? toValues(applied.purGroup),
      materialGroup: overrides.materialGroup ?? toValues(applied.matGroup),
      GMP: gmpValues,
      supplier:
        overrides.supplier ??
        (applied.supplier === "ALL" ? [] : [applied.supplier]),
    };

    if (nextPageNo !== undefined && nextPageNo !== null) {
      body.pageNo = nextPageNo;
    }
    if (nextPageSize !== undefined && nextPageSize !== null) {
      body.pageSize = nextPageSize;
    }

    return body;
  };

  const handleFilterOpen = () => {};

  const loadDashboardData = useCallback(async (appliedOverride, options = {}) => {
    const shouldShowLoader = Boolean(options.showLoader) || initialLoadRef.current;
    if (shouldShowLoader) {
      setShowLoader(true);
    }
    setRefreshing(true);
    try {
      const appliedState = appliedOverride ?? appliedFilters;
      const cacheKey = buildFiltersKey(appliedState);
      const existingRequest = dashboardLoadRequestCache.get(cacheKey);
      const requestPromise =
        existingRequest ||
        (async () => {
          const cachedTotal =
            dashboardTotalCountCache.get(cacheKey) ?? totalCountCacheRef.current.get(cacheKey);
          const initialPageSize = cachedTotal || INITIAL_SUMMARY_PAGE_SIZE;
          const initialResp = await dispatch(
            fetchAfcFilters({
              dynamicEndpoint: `${getAfcFilters}`,
              data: buildRequestBody(0, initialPageSize, {}, appliedOverride),
            })
          ).unwrap();

          let summaryResponse = initialResp;
          let rows = normalizeSummaryRows(summaryResponse);

          const rawTotalCount =
            initialResp?.data?.totalRecords ??
            initialResp?.totalRecords ??
            initialResp?.data?.totalCount ??
            initialResp?.totalCount;
          const initialTotalCount = Number(rawTotalCount) || rows.length || 0;
          let finalTotalCount = initialTotalCount;

          if (initialTotalCount > rows.length) {
            const fullResp = await dispatch(
              fetchAfcFilters({
                dynamicEndpoint: `${getAfcFilters}`,
                data: buildRequestBody(0, initialTotalCount, {}, appliedOverride),
              })
            ).unwrap();
            summaryResponse = fullResp;
            rows = normalizeSummaryRows(summaryResponse);

            const fullTotal =
              summaryResponse?.data?.totalRecords ??
              summaryResponse?.totalRecords ??
              summaryResponse?.data?.totalCount ??
              summaryResponse?.totalCount;
            finalTotalCount = Number(fullTotal) || initialTotalCount;
          }

          return {
            summaryResponse,
            rows,
            totalCountValue: finalTotalCount,
          };
        })();

      if (!existingRequest) {
        dashboardLoadRequestCache.set(cacheKey, requestPromise);
      }

      const { summaryResponse, rows, totalCountValue } = await requestPromise;
      setTotalCount(totalCountValue);
      totalCountCacheRef.current.set(cacheKey, totalCountValue);
      dashboardTotalCountCache.set(cacheKey, totalCountValue);

      // Always derive High/Medium/Low card counts from summary API payload.
      updateRiskCardsFromSummary(summaryResponse);
      setAllRows(rows);
      setUserDefinedRiskEdits((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          const rowKey = getRiskRowKey(row);
          if (!(rowKey in next)) {
            next[rowKey] = normalizeRiskValue(row?.userDefinedRiskStatus);
          }
        });
        return next;
      });
    } finally {
      const appliedState = appliedOverride ?? appliedFilters;
      const cacheKey = buildFiltersKey(appliedState);
      dashboardLoadRequestCache.delete(cacheKey);
      setRefreshing(false);
      if (shouldShowLoader) {
        setShowLoader(false);
        initialLoadRef.current = false;
      }
    }
  }, [dispatch, appliedFilters, getRiskRowKey, updateRiskCardsFromSummary]);

  const refreshRiskCardsAfterSave = useCallback(async () => {
    const response = await dispatch(
      fetchAfcFilters({
        dynamicEndpoint: `${getAfcFilters}`,
        // Fetch a minimal page for card-count refresh only; do not touch grid row state.
        data: buildRequestBody(0, 1),
      })
    ).unwrap();
    updateRiskCardsFromSummary(response);
  }, [dispatch, updateRiskCardsFromSummary, buildRequestBody]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const filteredRows = (allRows || []).filter((row) => {
      const matchesRisk =
        riskFilter === "ALL" || getEffectiveUserDefinedRisk(row).toLowerCase().includes(riskFilter);
      const matchesGmpFilter = matchesMulti(appliedFilters.gmp, row.gmp);
      return matchesRisk && matchesGmpFilter;
    });
    setFeedRows(filteredRows);

    // Build filter lists from the currently displayed rows so MultiDropdowns match the table
    const sourceRows = filteredRows || [];
    const plants = Array.from(new Set(sourceRows.map((r) => r.plant).filter((v) => v !== undefined && v !== null)));
    const suppliers = Array.from(new Set(sourceRows.map((r) => r.supplier).filter((v) => v !== undefined && v !== null)));
    const materials = Array.from(new Set(sourceRows.map((r) => r.material).filter((v) => v !== undefined && v !== null)));
    const purchasingGroups = Array.from(new Set(sourceRows.map((r) => r.purchasingGroup).filter((v) => v !== undefined && v !== null)));
    const materialGroupDescs = Array.from(new Set(sourceRows.map((r) => r.materialGroupDesc).filter((v) => v !== undefined && v !== null)));
    const mrpControl = Array.from(new Set(sourceRows.map((r) => r.mrpController).filter((v) => v !== undefined && v !== null)));
    const scodes = Array.from(new Set(sourceRows.map((r) => r.scode).filter((v) => v !== undefined && v !== null)));
    const gmps = Array.from(new Set(sourceRows.map((r) => r.gmp).filter((v) => v !== undefined && v !== null)));
    const country = Array.from(new Set(sourceRows.map((r) => r.country).filter((v) => v !== undefined && v !== null)));
    const region = Array.from(new Set(sourceRows.map((r) => r.region).filter((v) => v !== undefined && v !== null)));

    // Always populate filterData from the currently-displayed rows so MultiDropdowns
    // reflect exactly what's shown in the table (merge selected items to preserve selections)
    setFilterData((prev) => ({
      plants: mergeValues(prev.plants, mergeValues(plants, appliedFilters.plant.map((i) => i.value))),
      suppliers: mergeValues(prev.suppliers, mergeValues(suppliers, [appliedFilters.supplier].filter(Boolean))),
      materials: mergeValues(prev.materials, mergeValues(materials, appliedFilters.material.map((i) => i.value))),
      purchasingGroups: mergeValues(
        prev.purchasingGroups,
        mergeValues(purchasingGroups, appliedFilters.purGroup.map((i) => i.value))
      ),
      materialGroupDescs: mergeValues(
        prev.materialGroupDescs,
        mergeValues(materialGroupDescs, appliedFilters.matGroup.map((i) => i.value))
      ),
      mrpControl: mergeValues(prev.mrpControl, mergeValues(mrpControl, appliedFilters.mrpCtrl.map((i) => i.value))),
      scodes: mergeValues(prev.scodes, mergeValues(scodes, appliedFilters.scode.map((i) => i.value))),
      gmps: mergeValues(prev.gmps, mergeValues(gmps, appliedFilters.gmp.map((i) => i.value))),
      country: mergeValues(prev.country, mergeValues(country, appliedFilters.country.map((i) => i.value))),
      region: mergeValues(prev.region, mergeValues(region, appliedFilters.region.map((i) => i.value))),
    }));
  }, [allRows, riskFilter, appliedFilters, getEffectiveUserDefinedRisk]);

  const handleRefresh = () => {
    loadDashboardData(undefined, { showLoader: true });
  };

  const handleClearFilters = () => {
    const resetFilters = createInitialFilters();
    const resetApplied = createInitialFilters();
    setFilters(resetFilters);
    setAppliedFilters(resetApplied);
    setRiskFilter("ALL");
    dashboardFiltersCache = cloneFiltersState(resetFilters);
    dashboardAppliedFiltersCache = cloneFiltersState(resetApplied);
    dashboardRiskFilterCache = "ALL";
    hasRestoredGridFiltersRef.current = true;
    isRestoringGridFiltersRef.current = false;
    setGridFilterModel(null);
    setIsGridFilterHydrated(true);
    removeDashboardFiltersFromStorage();
    gridApi?.paginationGoToPage(0);
    gridApi?.setFilterModel(null);
    setGridHasFilters(false);
    localStorage.removeItem("savedVariantFilterModel");
  };

  const handleCreateVariantModal = () => {
    const currentFilter = gridApi?.getFilterModel?.() || {};
    setVariantData(currentFilter);
    setOpenCreateVariantModal(true);
  };

  const handleCloseCreateVariantModal = () => {
    setOpenCreateVariantModal(false);
  };

  const handleShowVariantModal = () => {
    setOpenShowVariantModal(true);
  };

  const handleCloseShowVariantModal = () => {
    setOpenShowVariantModal(false);
  };

  const onClickSelectVariant = (variant) => {
    if (!gridApi || !variant?.filterJson) return;

    try {
      const filterModel =
        typeof variant.filterJson === "string"
          ? JSON.parse(variant.filterJson)
          : variant.filterJson;

      localStorage.setItem("savedVariantFilterModel", JSON.stringify(filterModel));
      gridApi.setFilterModel(filterModel);
      gridApi.onFilterChanged();

      const hasFilters = filterModel && Object.keys(filterModel).length > 0;
      setGridHasFilters(hasFilters);
      setGridFilterModel(hasFilters ? filterModel : null);
    } catch (e) {
      console.error("Invalid filterJson:", variant.filterJson, e);
      toast.error("Unable to apply selected variant", { autoClose: 3000 });
    }
  };

  const handleGridFilterChanged = useCallback((params) => {
    if (isRestoringGridFiltersRef.current) {
      return;
    }
    const model = params?.api?.getFilterModel?.() || {};
    const hasFilters = Object.keys(model).length > 0;
    setGridHasFilters(hasFilters);
    setGridFilterModel(hasFilters ? model : null);
  }, []);

  const handlePaginationChanged = useCallback(() => {
    if (!gridApi) return;
    const nextPageSize = gridApi.paginationGetPageSize();
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
    }
  }, [gridApi, pageSize]);

  const handleCellClicked = (params) => {
    const { colDef, data } = params || {};
    if (colDef?.field === "material" && data?.material && data?.plant) {
      navigate(
        `/dashboard/material/${encodeURIComponent(data.material)}/${encodeURIComponent(data.plant)}`,
        {
          state: {
            description: data.descriptions,
            supplier: data.supplier,
            unit: data.unit,
            materialGroupDesc: data.materialGroupDesc,
            purchasingGroup: data.purchasingGroup,
            mrpController: data.mrpController,
          },
        }
      );
    }
  };

  const getUserDefinedRiskValue = useCallback(
    (row) => {
      const key = getRiskRowKey(row);
      const edited = userDefinedRiskEdits[key];
      if (edited !== undefined) return edited;
      return normalizeRiskValue(row?.userDefinedRiskStatus);
    },
    [getRiskRowKey, userDefinedRiskEdits]
  );

  const onUserDefinedRiskChange = useCallback(
    (row, value) => {
      const key = getRiskRowKey(row);
      const nextRisk = normalizeRiskValue(value);
      setUserDefinedRiskEdits((prev) => ({
        ...prev,
        [key]: nextRisk,
      }));
      // params.node.setDataValue in the cell renderer already updates
      // the exact row node in the grid — no full re-render needed here.
    },
    [getRiskRowKey]
  );

  const isRiskRowSaving = useCallback((rowKey) => Boolean(riskRowSaving[rowKey]), [riskRowSaving]);

  const onUserDefinedRiskSave = useCallback(
    async (row, selectedRiskOverride) => {
      const rowKey = getRiskRowKey(row);
      const selectedRisk = normalizeRiskValue(selectedRiskOverride) || getUserDefinedRiskValue(row);
      if (!selectedRisk || !row?.plant || !row?.material) {
        return;
      }

      setRiskRowSaving((prev) => ({ ...prev, [rowKey]: true }));
      try {
        const response = await dispatch(
          saveRiskAuditHistory({
            data: {
              tableType: TABLE_TYPE,
              plant: row.plant,
              material: row.material,
              riskstatusUserDefine: selectedRisk,
            },
          })
        ).unwrap();

        const payload = response?.data || response || {};
        const nextRisk = normalizeRiskValue(
          payload?.riskStatus ??
            payload?.riskStatusUserDefined ??
            payload?.riskstatusUserDefine ??
            payload?.userDefinedRiskStatus ??
            selectedRisk
        );
        const nextUpdatedBy = payload?.updatedBy ?? row?.updatedBy ?? [];

        const applyRowUpdates = (rows = []) =>
          rows.map((item) => {
            if (getRiskRowKey(item) !== rowKey) return item;
            return {
              ...item,
              userDefinedRiskStatus: nextRisk,
              updatedBy: normalizeUpdatedByEntries(nextUpdatedBy),
            };
          });

        setAllRows((prev) => applyRowUpdates(prev));
        setFeedRows((prev) => applyRowUpdates(prev));
        setUserDefinedRiskEdits((prev) => ({
          ...prev,
          [rowKey]: nextRisk,
        }));

        // After save succeeds, refresh only card counts from summary API.
        try {
          await refreshRiskCardsAfterSave();
        } catch {
          // Keep row update success unaffected even if count refresh fails.
        }

        toast.success("Risk status defined successfully", { autoClose: 4000 });
      } catch {
        toast.error("Something went wrong", { autoClose: 4000 });
      } finally {
        setRiskRowSaving((prev) => ({ ...prev, [rowKey]: false }));
      }
    },
    [dispatch, getRiskRowKey, getUserDefinedRiskValue, refreshRiskCardsAfterSave]
  );

  const gridContext = useMemo(
    () => ({
      getRiskRowKey,
      getUserDefinedRiskValue,
      onUserDefinedRiskChange,
      onUserDefinedRiskSave,
      isRiskRowSaving,
      canEditUserDefinedRisk,
    }),
    [getRiskRowKey, getUserDefinedRiskValue, onUserDefinedRiskChange, onUserDefinedRiskSave, isRiskRowSaving, canEditUserDefinedRisk]
  );

  const columnDefs = useMemo(
    () =>
      ARDashboardColumns().map((col) => ({
        ...col,
        hide: hiddenColumns.includes(col.field),
      })),
    [hiddenColumns]
  );

  const columnOptions = useMemo(
    () =>
      (columnDefs || [])
        .filter((col) => col.field)
        .map((col) => ({
          title: col.headerName || col.field,
          value: col.field,
          name: String(col.field),
        })),
    [columnDefs]
  );

const defaultColDef = useMemo(() => ({
  resizable: true,
  filter: 'agSetColumnFilter',
  filterParams: {
    excelMode: 'windows',
    suppressMiniFilter: false,
    buttons: ['apply', 'reset'],
  },
 valueFormatter: (params) => {
                const node = params?.node;
                if (!node || node.data === undefined || node.data === null) return "";
                const value = params?.value;
                if (
                  value === null ||
                  value === undefined ||
                  value === "" ||
                  String(value).trim() === "-"
                ) {
                  return "N/A";
                }
                return value;
              },
}), []);



  const openColumnSelector = (event) => {
    const selected = hiddenColumns
      .map((field) => columnOptions.find((option) => option.value === field))
      .filter(Boolean);
    setPendingHiddenColumns(selected);
    setColumnSelectorAnchor(event.currentTarget);
  };

  const closeColumnSelector = () => setColumnSelectorAnchor(null);

  const handleHiddenColumnsChange = (_name, items) => {
    const cleanedItems = (items || []).filter((item) => item?.title !== "Select All");
    setPendingHiddenColumns(cleanedItems);
  };

  const applyHiddenColumns = () => {
    const hiddenFields = (pendingHiddenColumns || []).map((item) => item.value);
    if (columnApi) {
      columnOptions.forEach((option) => {
        columnApi.setColumnVisible(option.value, !hiddenFields.includes(option.value));
      });
    }
    setHiddenColumns(hiddenFields);
    setColumnSelectorAnchor(null);
  };

  const handleExportCsv = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: "material-confirmation.csv",
      });
    }
  };

  const handleExportExcel = () => {
    if (gridApi) {
      gridApi.exportDataAsExcel({
        fileName: "material-confirmation.xlsx",
        sheetName: "Confirmation",
      });
    }
  };

  const toUniqueSorted = (items = []) =>
    Array.from(new Set(items.filter((v) => v !== undefined && v !== null && v !== ""))).sort((a, b) =>
      String(a).localeCompare(String(b))
    );

  const toOptions = (items) => [
    { value: "ALL", label: "ALL" },
    ...toUniqueSorted(items).map((v) => ({ value: v, label: v })),
  ];

  const toMultiOptions = (items) =>
    toUniqueSorted(items).map((v) => ({ title: v, value: v, name: String(v) }));

  const plantOptions = useMemo(() => toMultiOptions(filterData.plants), [filterData.plants]);
  const supplierOptions = useMemo(() => toOptions(filterData.suppliers), [filterData.suppliers]);
  const materialOptions = useMemo(() => toMultiOptions(filterData.materials), [filterData.materials]);
  const matGroupOptions = useMemo(() => toMultiOptions(filterData.materialGroupDescs), [filterData.materialGroupDescs]);
  const purGroupOptions = useMemo(() => toMultiOptions(filterData.purchasingGroups), [filterData.purchasingGroups]);
  const scodeOptions = useMemo(() => toMultiOptions(filterData.scodes), [filterData.scodes]);
  const mrpCtrlOptions = useMemo(() => toMultiOptions(filterData.mrpControl), [filterData.mrpControl]);
  const gmpOptions = useMemo(() => toMultiOptions(filterData.gmps), [filterData.gmps]);
  const countryOptions = useMemo(() => toMultiOptions(filterData.country), [filterData.country]);
  const regionOptions = useMemo(() => toMultiOptions(filterData.region), [filterData.region]);

  const matchesMulti = (selectedItems, value) =>
    !selectedItems?.length || selectedItems.some((item) => item.value === value);

  // client-side mode: data is provided via `feedRows` and loaded in `loadDashboardData`

  const filterHeight = "24px";
  const filterSx = {
    "& .MuiInputLabel-root": { fontSize: "13px" },
    "& .MuiSelect-select": { fontSize: "12px" },
  };

  const isSearchDisabled =
    !filters.plant.length &&
    filters.supplier === "ALL" &&
    !filters.material.length &&
    !filters.matGroup.length &&
    !filters.purGroup.length &&
    !filters.scode.length &&
    !filters.mrpCtrl.length &&
    !filters.gmp.length &&
    !filters.country.length &&
    !filters.region.length;

  const isClearDisabled = isSearchDisabled && !gridHasFilters && riskFilter === "ALL";

  return (
    <>
      <div className="ar-dashboard-topsection">
        <TopSection
          labelList={[
            
            { title: "Dashboard", path: "/dashboard" },
          ]}
          hideHeader={true}
          headerName="Supplier Forecast Confirmation Analytics"
          rightContent={
            <ButtonComponent
              label="Refresh"
              type="primary"
              bgColor="#e70d0dff"
              color="#ffffff"
              onClick={handleRefresh}
              disabled={refreshing}
            />
          }
        />
      </div>

      <div>
        <div className={`ar-dashboard-cards card-container ${riskFilter === "ALL" ? "risk-all" : ""}`}>
          <Cards
            cards={cardsData}
            showBorder={true}
            callBackFunction={handleCardFilter}
            showClearPill={true}
            isFiltered={riskFilter !== "ALL"}
            clearPillText="Filtered - click to clear"
            onClearPill={() => setRiskFilter("ALL")}
          />
        </div>
      </div>

      <div className="ar-dashboard-feed-section">
        <div className="ar-dashboard-feed-header">
          <div className="ar-dashboard-feed-title">Supply Risk Feed (Plant-Material Level)</div>

          <div className="ar-dashboard-filter-bar">
             <Filter label="Region">
              <MultiDropdown
                name="region"
                options={regionOptions}
                selectedItemsData={filters.region}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
              />
            </Filter>
            <Filter label="Plant Code">
              <MultiDropdown
                name="plant"
                options={plantOptions}
                selectedItemsData={filters.plant}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
                minWidth="180px"
              />
            </Filter>
             <Filter label="Purchase Group">
              <MultiDropdown
                name="purGroup"
                options={purGroupOptions}
                selectedItemsData={filters.purGroup}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
              />
            </Filter>
             <Filter label="MRP Controller">
              <MultiDropdown
                name="mrpCtrl"
                options={mrpCtrlOptions}
                selectedItemsData={filters.mrpCtrl}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
              />
            </Filter>
            <Filter label="GMP">
              <MultiDropdown
                name="gmp"
                options={gmpOptions}
                selectedItemsData={filters.gmp}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
              />
            </Filter>
            <Filter label="Material Group Description">
              <MultiDropdown
                name="matGroup"
                options={matGroupOptions}
                selectedItemsData={filters.matGroup}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
              />
            </Filter>
            <Filter label="External Material Group">
              <MultiDropdown
                name="scode"
                options={scodeOptions}
                selectedItemsData={filters.scode}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
              />
            </Filter>

            <Filter label="TCCC Material Number">
              <MultiDropdown
                name="material"
                options={materialOptions}
                selectedItemsData={filters.material}
                onChangeItems={handleMultiFilterChange}
                  onApplyItems={applyFilterSelection}
                  showApply={true}
                onOpenDropdown={handleFilterOpen}
                dropdownWidth="260px"
                minWidth="180px"
              />
            </Filter>
{/*
            <Filter label="Country">
              <MultiDropdown
                name="country"
                options={countryOptions}
                selectedItemsData={filters.country}
                onChangeItems={handleMultiFilterChange}
                dropdownWidth="260px"
                minWidth="180px"
              />
            </Filter>
*/}
           

            


            

           
          </div>
          <div className="ar-dashboard-feed-actions-row">
            <div className="ar-dashboard-feed-actions">
              <Badge
                badgeContent={hiddenColumns.length}
                color="primary"
                overlap="circular"
                sx={{ mr: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={openColumnSelector}
                  style={{
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    padding: 4,
                    backgroundColor: "#ffffff",
                  }}
                  aria-label="Hide Columns"
                  aria-haspopup="true"
                  aria-controls={columnSelectorAnchor ? "hide-columns-popover" : undefined}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Badge>
              <ButtonComponent
                label="Create/Edit Variant"
                type="secondary"
                bgColor="#111827"
                color="#ffffff"
                onClick={handleCreateVariantModal}
                maxWidth="170px"
              />
              <ButtonComponent
                label="Show Variants"
                type="secondary"
                bgColor="#111827"
                color="#ffffff"
                onClick={handleShowVariantModal}
                maxWidth="140px"
              />
              <ButtonComponent
                label="Clear Filters"
                type="secondary"
                bgColor="#e70d0dff"
                color="#ffffff"
                onClick={handleClearFilters}
                disabled={isClearDisabled}
              />
            </div>
          </div>
        </div>

        <div style={{ position: "relative", minHeight: 400 }}>
          {!showLoader ? (
            <DataTable
              height={400}
              columns={columnDefs}
              rows={feedRows}
              paginationProp={pagination}
              paginationPageSize={pageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              onPaginationChanged={handlePaginationChanged}
              onFilterChanged={handleGridFilterChanged}
              onFirstDataRendered={handleFirstDataRendered}
              defaultColDef={defaultColDef}
              getRowId={(params) => `${params?.data?.plant || ""}::${params?.data?.material || ""}`}
              onGridReady={onGridReady}
              gridOptions={{
                context: gridContext,
                onCellClicked: handleCellClicked,
           //     overlayNoRowsTemplate: errorOverlayTemplate,
              }}
            />
          ) : null}

          {showLoader || !isGridFilterHydrated ? (
            <Loader className="ar-dashboard-loader" />
          ) : null}
        </div>
      </div>
      <Popover
        id="hide-columns-popover"
        open={Boolean(columnSelectorAnchor)}
        anchorEl={columnSelectorAnchor}
        onClose={closeColumnSelector}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ style: { padding: 12, width: 360 } }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
          Hide Columns
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <MultiDropdown
            name="hideColumns"
            options={columnOptions}
            selectedItemsData={pendingHiddenColumns}
            onChangeItems={handleHiddenColumnsChange}
            dropdownWidth="320px"
            minWidth="220px"
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <ButtonComponent
              label="Cancel"
              type="secondary"
              bgColor="#e2e8f0"
              color="#111827"
              onClick={closeColumnSelector}
            />
            <ButtonComponent
              label="Save"
              type="primary"
              bgColor="#1976d2"
              color="#ffffff"
              onClick={applyHiddenColumns}
            />
          </div>
        </div>
      </Popover>
      <VariantViewModal
        tableType={TABLE_TYPE}
        openCreateVariantModal={openCreateVariantModal}
        handleCloseCreateVariantModal={handleCloseCreateVariantModal}
        data={variantData}
      />
      <ShowVariantViewModal
        tableType={TABLE_TYPE}
        openShowVariantModal={openShowVariantModal}
        handleCloseShowVariantModal={handleCloseShowVariantModal}
        onClickSelectVariant={onClickSelectVariant}
      />
    </>
  );
};

/* =======================
   FILTER WRAPPER
   ======================= */
const Filter = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%", minWidth: 0 }}>
    <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
    <div style={{ width: "100%" }}>{children}</div>
  </div>
);

export default ARDashboard;